import type {
  LiveUpdatesChannel,
  LiveUpdatesSection,
  LiveUpdatesStatusKey,
} from 'api/liveUpdates.types'

// Stable tracker-stage keys, independent of the localized display copy. Several
// statuses can share one stage (picked_up/en_route/arrived all render as
// 'on_the_way'); the two canceled statuses map to no tracker stage. Stage
// identity + matching (active dot, disabled-stage hiding) key on THESE, never on
// t('tracker.*') label strings — so a copy edit, a backend label tweak, or
// localization can never silently break the tracker. Labels are resolved via
// t(`tracker.${stage}`) only at render time (the tracker.* locale keys are these
// same keys, minus 'canceled', which never appears in a track).
export type TrackerStage =
  | 'placed'
  | 'preparing'
  | 'ready'
  | 'on_the_way'
  | 'delivered'
  | 'canceled'

export const STATUS_STAGE: Record<LiveUpdatesStatusKey, TrackerStage> = {
  placed: 'placed',
  preparing: 'preparing',
  ready: 'ready',
  picked_up: 'on_the_way',
  en_route: 'on_the_way',
  arrived: 'on_the_way',
  delivered: 'delivered',
  canceled_by_restaurant: 'canceled',
  canceled_by_delivery: 'canceled',
}

// The slice of the focused status the preview column needs to mirror form
// state. The scene projects the currently-focused status (merchant edits +
// defaults) into this shape; the preview renders literally from it — {token}
// variables such as {location_name} are shown verbatim (matching Figma).
export type PreviewContent = {
  statusKey: LiveUpdatesStatusKey
  // Stable tracker-stage key for the focused status (derived from statusKey via
  // STATUS_STAGE) — drives which dot/row is active. 'canceled' matches no track
  // stage, so a canceled status highlights nothing.
  stage: TrackerStage
  // Merchant-editable status title.
  title: string
  // Merchant-editable push-message body.
  pushText: string
  // Merchant-editable Live Activity / home-screen text.
  liveText: string
  section: LiveUpdatesSection
  channels: LiveUpdatesChannel[]
}

export type PreviewPlatform = 'ios' | 'android'

export type PreviewTab = 'push_live_activity' | 'in_app'

// Shared, pre-derived state the LiveActivityCard dispatcher hands each
// platform card (iOS / Android). Track derivation happens once in the
// dispatcher so both cards render from the same facts.
export type LiveActivityPlatformProps = {
  content: PreviewContent
  // The merchant's uploaded logo; falls back to the brand-red placeholder
  // square when omitted (no logo uploaded).
  appIconUrl?: string
  // The derived tracker track (disabled middle stages already dropped).
  track: TrackerStage[]
  // Index of content.stage within `track`; -1 for an out-of-track (canceled)
  // stage, which highlights no progress.
  activeIndex: number
  // FR-03: only "Order placed" remains enabled — no tracker is rendered.
  confirmedOnly: boolean
}
